import {Component, Input, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {
  ORGANISER_EVENTS, ORGANISER_MESSAGES,
} from "src/app/app-routing.module";
import {TranslocoService} from "@ngneat/transloco";
import {
  AdministratorsService,
  AuthenticationService,
  EventService, LeagueService,
  OrganiserService,
  RaceService,
  RequestService,
  UserDetailService
} from "@services";
import {
  EventItem,
  JwtResponse,
  Message,
  Organiser,
  ORGANIZER_ROLE,
  Race,
  UserDetail, Request, League
} from "@model";
import {getLanguageValue} from "@utility";
import {AbstractComponent} from "@components";

@Component({
  selector: 'app-admin-page-template',
  templateUrl: './admin-template.component.html',
  styleUrls: ['./admin-template.component.scss']
})
export class AdminTemplateComponent extends AbstractComponent implements OnInit {
  @Input() additionalClass: string | undefined;
  @Input() unreadMessages: Message[] | undefined;
  currentLoggedInUser: UserDetail | undefined;
  currentLoggedInOrganiser: Organiser | undefined;
  isMobileMenuActive: boolean = false;
  currentEvent: EventItem | undefined;
  currentRace: Race | undefined;
  currentOrganiser: Organiser | undefined;
  currentRequest: Request | undefined;
  currentUserDetails: UserDetail | undefined;
  currentLeague: League | undefined;
  pageTitle: string = "";
  organiserId: number | undefined;

  constructor(private router: Router, public authenticationService: AuthenticationService, private requestService: RequestService,
              private userDetailsService: UserDetailService, private organiserService: OrganiserService, private adminService: AdministratorsService,
              private eventService: EventService, private translocoService: TranslocoService, private raceService: RaceService,
              private route: ActivatedRoute, private leagueService: LeagueService) {
    super();
  }

  ngOnInit(): void {
    this.subscriptions.push(this.router.events.subscribe(() => {
        this.pageTitle = this.getPageTitle();
      }
    ));
    this.subscriptions.push(this.authenticationService.jwtResponse.subscribe((jwtResponse: JwtResponse | null) => {
      if (!!jwtResponse && !!jwtResponse!.roles && jwtResponse!.roles!.length > 0) {
        if (jwtResponse!.roles.includes(ORGANIZER_ROLE)) {
          this.organiserService.getLoggedInOrganiser();
        }
      }
    }));
    this.subscriptions.push(this.organiserService.loggedInOrganiser.subscribe((organiser: Organiser | undefined) => {
      this.currentLoggedInOrganiser = organiser;
    }));

    this.subscriptions.push(this.userDetailsService.currentUserDetails.subscribe((userDetails: UserDetail | undefined) => {
      this.currentLoggedInUser = userDetails;
    }));

    this.subscriptions.push(this.eventService.currentEvent.subscribe((eventItem: EventItem | undefined) => {
      this.currentEvent = eventItem;
      this.pageTitle = this.getPageTitle();
    }));

    this.subscriptions.push(this.raceService.currentRace.subscribe((race: Race | undefined) => {
      this.currentRace = race;
      this.pageTitle = this.getPageTitle();
    }));

    this.subscriptions.push(this.organiserService.currentOrganiser.subscribe((organiser: Organiser | undefined) => {
      this.currentOrganiser = organiser;
      this.pageTitle = this.getPageTitle();
    }));

    this.subscriptions.push(this.requestService.currentRequest.subscribe((request: Request | undefined) => {
      this.currentRequest = request;
      this.pageTitle = this.getPageTitle();
    }));

    this.subscriptions.push(this.adminService.currentUserDetails.subscribe((userDetail: UserDetail | undefined) => {
      this.currentUserDetails = userDetail;
    }));

    this.subscriptions.push(this.leagueService.currentLeague.subscribe((league: League | undefined) => {
      this.currentLeague = league;
      this.pageTitle = this.getPageTitle();
    }));
  }

  getCurrentUrl(): string {
    return document.location.href;
  }

  goToMessages(messageFilter: boolean) {
    this.router.navigate([ORGANISER_MESSAGES], {queryParams: {read: messageFilter}});
  }

  onClickToLogoImage(): void {
    this.goToOrganiserEvents();
  }

  goToOrganiserEvents(): void {
    this.closeMobileMenu();
    this.router.navigate([ORGANISER_EVENTS]);
  }

  closeMobileMenu(): void {
    if (this.isMobileMenuActive)
      this.isMobileMenuActive = !this.isMobileMenuActive;
  }

  private getPageTitle(): string {
    if (this.getCurrentUrl().includes("dashboard")) {
      return this.translocoService.translate("app.common.dashboard");
    } else if (this.getCurrentUrl().includes("categories")) {
      return this.translocoService.translate("app.common.categories.list");
    } else if (this.getCurrentUrl().includes("discounts")) {
      return this.translocoService.translate("app.common.discounts.management");
    } else if (this.getCurrentUrl().includes("events")) {
      return this.getPageTitleForEventAndRace(this.translocoService.translate("app.common.events"));
    } else if (this.getCurrentUrl().includes("measurements")) {
      return this.getPageTitleForEventAndRace(this.translocoService.translate("app.common.measurements"));
    } else if (this.getCurrentUrl().includes("organisers")) {
      return this.getPageTitleForOrganiser();
    } else if (this.getCurrentUrl().includes("discountsDetails")) {
      this.organiserId = this.route.snapshot.queryParams['organiserId'];
      if (this.organiserId === undefined) {
        return this.translocoService.translate("app.common.discounts.management") + " - " + this.translocoService.translate("administrator.discounts.discount.details");
      }
      return this.getPageTitleForCurrentOrganiser() + " - " + this.translocoService.translate("administrator.discounts.discount.details");
    } else if (this.getCurrentUrl().includes("profile")) {
      return this.translocoService.translate("app.common.organizers");
    } else if (this.getCurrentUrl().includes("requests")) {
      return this.getPageTitleForRequests();
    } else if (this.getCurrentUrl().includes("administrators")) {
      return this.translocoService.translate("app.common.administrators");
    } else if (this.getCurrentUrl().includes("users")) {
      return this.getPageTitleForUsers();
    } else if (this.getCurrentUrl().includes("messages")) {
      return this.translocoService.translate("app.common.messages");
    } else if (this.getCurrentUrl().includes("accountStatementsDetails")) {
      return this.translocoService.translate("app.common.accountStatements") + " - " + this.translocoService.translate("app.common.accountStatements.details");
    } else if (this.getCurrentUrl().includes("accountStatements")) {
      return this.translocoService.translate("app.common.accountStatements");
    } else if (this.getCurrentUrl().includes("leagues")) {
      return this.getPageTitleForLeague();
    } else if (this.getCurrentUrl().includes("preview")) {
      return this.getPageTitleForEventAndRace(this.translocoService.translate("app.common.events"));
    }
    return "";
  }

  private getPageTitleForUsers() {
    if (this.currentUserDetails) {
      let suffix: string = "";
      if (this.currentUserDetails.firstname) {
        suffix += " - " + this.currentUserDetails.firstname
      }
      if (this.currentUserDetails.lastname) {
        suffix += (suffix !== "") ? " " + this.currentUserDetails.lastname : " - " + this.currentUserDetails.lastname;
      }
      if (suffix === "") {
        suffix += " - " + this.currentUserDetails.userId;
      }
      return this.translocoService.translate("app.common.user") + suffix;
    } else {
      return this.translocoService.translate("app.common.users");
    }
  }

  private getPageTitleForRequests() {
    var title: string = this.translocoService.translate("app.common.list.of.requests");
    if (this.currentRequest && this.currentRequest?.organiserRequest?.name) {
      title = title + " - " + this.currentRequest?.organiserRequest?.name;
    }
    return title;
  }

  private getPageTitleForOrganiser() {
    var title: string = this.translocoService.translate("app.common.organizers");
    if (this.currentOrganiser) {
      title = title + " - " + this.currentOrganiser.name;
    }
    return title;
  }

  private getPageTitleForCurrentOrganiser() {
    if (this.currentOrganiser === undefined) {
      this.organiserId = +this.route.snapshot.queryParams['organiserId'];
      this.organiserService.getOrganiser(this.organiserId)
    }
    var title: string = this.translocoService.translate("app.common.organizers");
    if (this.currentOrganiser) {
      title = title + " - " + this.currentOrganiser.name;
    }
    return title;
  }

  private getPageTitleForEventAndRace(title: string) {
    if (this.getCurrentUrl().includes("preview")) {
      title = this.translocoService.translate("event.item.card.preview");
    }
    if (this.currentEvent) {
      let eventName: string | undefined = getLanguageValue(this.currentEvent.name,
        this.currentEvent.nameEng, this.currentEvent.nameIta,
        this.currentEvent.nameSlo, this.currentEvent.nameSr, this.currentEvent.nameBs, this.translocoService.getActiveLang(),
        this.currentEvent.name)
      if (eventName && eventName.length > 0) {
        title = title + " - " + eventName;
      }
    }
    if (this.currentRace && (this.getCurrentUrl().includes("race") || this.getCurrentUrl().includes("preview"))) {
      let raceName: string | undefined = getLanguageValue(this.currentRace.name,
        this.currentRace.nameEng, this.currentRace.nameIta,
        this.currentRace.nameSlo, this.currentRace.nameSr, this.currentRace.nameBs, this.translocoService.getActiveLang(),
        this.currentRace.name)
      if (raceName && raceName.length > 0) {
        title = title + " - " + raceName;
      }
    }
    return title;
  }

  private getPageTitleForLeague() {
    var title: string = this.translocoService.translate("app.common.league.series");
    if (this.currentLeague) {
      let leagueName: string | undefined = getLanguageValue(this.currentLeague.name,
        this.currentLeague.nameEng, this.currentLeague.nameIta,
        this.currentLeague.nameSlo, this.currentLeague.nameSr,this.currentLeague.nameBs, this.translocoService.getActiveLang(),
        this.currentLeague.name)
      if (leagueName && leagueName.length > 0) {
        title = title + " - " + leagueName;
      }
    }
    return title;
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.eventService.currentEvent.next(undefined);
    this.raceService.currentRace.next(undefined);
    this.organiserService.emptyOrganisers();
  }
}
